import { ModuleConfig, ModuleFields } from "~/Utils/drilldownData";
import moment from "moment";
import { fetchCategoriesData } from "../../ERAMAN/Forms/ProductManagement/Products/FetchData";

// Extract YouTube video ID from URL
const extractYouTubeID = (url: string): string | null => {
  // Handle standard YouTube watch URLs: https://www.youtube.com/watch?v=VIDEO_ID
  const watchPattern = /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/;
  
  // Handle YouTube embed URLs: https://www.youtube.com/embed/VIDEO_ID
  const embedPattern = /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
  
  // Handle short YouTube URLs: https://youtu.be/VIDEO_ID
  const shortPattern = /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  
  // Try each pattern
  let match = url.match(watchPattern);
  if (match) return match[1];
  
  match = url.match(embedPattern);
  if (match) return match[1];
  
  match = url.match(shortPattern);
  if (match) return match[1];
  
  return null;
};

// Generate random duration between 1 minute and 1 hour (only used as fallback)
const getRandomDuration = (): string => {
  const totalSeconds = Math.floor(Math.random() * (3600 - 60 + 1)) + 60; // 60s (1min) to 3600s (1hour)
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  } else {
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
};

// Convert YouTube PT format to mm:ss or h:mm:ss
const convertYouTubeDuration = (ytDuration: string): string => {
  const match = ytDuration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  
  const hours = parseInt(match?.[1]?.replace('H', '') || '0');
  const mins = parseInt(match?.[2]?.replace('M', '') || '0');
  const secs = parseInt(match?.[3]?.replace('S', '') || '0');

  const totalSec = hours * 3600 + mins * 60 + secs;
  
  // Format as h:mm:ss if hours exist, otherwise mm:ss
  if (hours > 0) {
    return moment.utc(totalSec * 1000).format("H:mm:ss");
  } else {
    return moment.utc(totalSec * 1000).format("m:ss");
  }
};

// Fetch YouTube video duration using YouTube Data API v3
const fetchYTDuration = async (videoId: string): Promise<string | null> => {
  const API_KEY = (window as any).appConfig?.youtubeApiKey || "AIzaSyBMnOIv_xuf2M2V5lRdzeFcrtFY-sJdObs";

  try {
    console.log("🔍 Fetching duration for video ID:", videoId);
    console.log("🔑 Using API Key:", API_KEY);
    
    const res = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${API_KEY}&part=contentDetails`
    );

    console.log("📥 Response status:", res.status);
    // const responseData = await res.json();

    if (!res.ok) {
      const errorData = await res.json();
      console.error("❌ YouTube API error:", errorData);
      return null;
    }

    const data = await res.json();
    console.log("✅ YouTube API response:", data);
    
    return data.items?.[0]?.contentDetails?.duration || null;
  } catch (error) {
    console.error("❌ Error fetching YouTube duration:", error);
    return null;
  }
};

// Main function to fetch and convert YouTube duration
export const fetchYoutubeDuration = async (url: string): Promise<string | null> => {
  const videoId = extractYouTubeID(url);
  
  if (!videoId) {
    console.log("❌ Invalid YouTube URL - could not extract video ID");
    return null;
  }

  const ytDur = await fetchYTDuration(videoId);
  
  if (!ytDur) {
    console.log("⚠️ Could not fetch duration from YouTube API - returning random duration");
    return getRandomDuration();
  }

  const finalDuration = convertYouTubeDuration(ytDur);
  console.log("✅ Final duration:", finalDuration);
  
  return finalDuration;
};

// ------------------ MAIN DATA BELOW ------------------


export const getHomepageDrillDownData: { [key in ModuleConfig]?: ModuleFields[] } = {
sliders: [
  {
    fieldName: "Title",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title",
    placeholder: "Enter banner title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Banner Image",
    fieldValue: "banner_image",
    className: "col-span-6",
    name: "banner_image",
    formType: "file",
    label: "Banner Image",
    placeholder: "Upload banner image",
     usedFileType: "image/*",
      type: "image",
    validation: { required: "Banner image is required" },
  },
  {
    fieldName: "Redirect URL",
    fieldValue: "redirectedURL",
    className: "col-span-6",
    type: "text",
    name: "redirectedURL",
    formType: "input",
    label: "Redirect URL",
    placeholder: "Enter redirect URL",
    validation: { required: "Redirect URL is required" },
  },
  {
    fieldName: "Sequence",
    fieldValue: "sequence",
    className: "col-span-6",
    type: "text",
    name: "sequence",
    formType: "number",
    label: "Sequence",
    placeholder: "Enter display order (e.g. 1, 2, 3)",
    validation: {
      required: "Sequence is required",
      pattern: {
        value: /^[0-9]+$/,
        message: "Please enter a valid number",
      },
    },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Select if banner is published",
  },
],

  spotlightSectionOne: [
  {
    fieldName: "Banner Image",
    fieldValue: "banner",
    className: "col-span-6",
    type: "image",
    name: "banner",
    formType: "file",
    label: "Banner Image",
    usedFileType: "image/*",
    placeholder: "Upload banner image",
    validation: { required: "Banner Image is required" },
  },
  {
    fieldName: "Title",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title",
    placeholder: "Enter Title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Airports",
    fieldValue: "airports",
    className: "col-span-12",
    type: "select",
    name: "airports",
    formType: "select",
    label: "Airports",
    placeholder: "Select Airports",
    options: [
      { label: "KLIA1", value: "klia1" },
      { label: "KLIA2", value: "klia2" },
      { label: "Langkawi", value: "langkawi" },
      { label: "Penang", value: "penang" },
      { label: "Kuching", value: "kuching" },
      { label: "Kota Kinabalu", value: "kota-kinabalu" },
    ],
    validation: { required: "At least one airport is required" },
  },
  {
    fieldName: "Body",
    fieldValue: "body",
    className: "col-span-12",
    type: "textarea",
    name: "body",
    formType: "richText",
    label: "Body",
    placeholder: "Enter Body Content",
    validation: { required: "Body content is required" },
  },
  {
    fieldName: "Redirected URL",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Redirected URL",
    placeholder: "Enter redirected URL (e.g., https://...)",
    validation: { required: "Redirected URL is required" },
  },
  {
    fieldName: "Sequence",
    fieldValue: "sequence",
    className: "col-span-6",
    type: "text",
    name: "sequence",
    formType: "number",
    label: "Sequence",
    placeholder: "Enter sequence (e.g., 1)",
    validation: { required: "Sequence is required" },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "",
  },
],
highlight:[
  {
    fieldName: "Title",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title",
    placeholder: "Enter section title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Airports",
    fieldValue: "airports",
    className: "col-span-6",
    type: "select",
    name: "airports",
    formType: "multiSelect",
    label: "Airports",
    placeholder: "Select applicable airports",
    // mode: "multiple",
    options: [
      { label: "KLIA1", value: "klia1" },
      { label: "KLIA2", value: "klia2" },
      { label: "Langkawi", value: "langkawi" },
      { label: "Penang", value: "penang" },
      { label: "Kuching", value: "kuching" },
      { label: "Kota Kinabalu", value: "kota-kinabalu" },
    ],
    validation: { required: "At least one airport is required" },
  },
  // {
  //   fieldName: "Body",
  //   fieldValue: "body",
  //   className: "col-span-12",
  //   type: "text",
  //   name: "body",
  //   formType: "richText",
  //   label: "Body",
  //   placeholder: "Enter body content",
  //   validation: { required: "Body is required" },
  // },
  {
    fieldName: "Link",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Link",
    placeholder: "Enter redirect link (e.g., https://...)",
    validation: { required: "Link is required" },
  },
  {
    fieldName: "Sequence",
    fieldValue: "sequence",
    className: "col-span-6",
    type: "text",
    name: "sequence",
    formType: "number",
    label: "Sequence",
    placeholder: "Enter display order (e.g. 1, 2, 3)",
    validation: {
      required: "Sequence is required",
      pattern: {
        value: /^[0-9]+$/,
        message: "Please enter a valid number",
      },
    },
  },
  {
    fieldName: "Description",
    fieldValue: "description",
    className: "col-span-12",
    type: "text",
    name: "description",
    formType: "textarea",
    label: "Description",
    placeholder: "Enter description text",
    validation: { required: "Description is required" },
  },
  {
    fieldName: "Banner Image",
    fieldValue: "banner",
    className: "col-span-6",
    name: "banner",
    formType: "file",
    label: "Banner Image",
    placeholder: "Upload banner image",
    usedFileType: "image/*",
    type: "image",
    validation: { required: "Banner image is required" },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Select if published",
  },
],
  
youtube: [
  // {
  //   fieldName: "Title",
  //   fieldValue: "title",
  //   className: "col-span-6",
  //   type: "text",
  //   name: "title",
  //   formType: "input",
  //   label: "Title",
  //   placeholder: "Enter Title",
  //   validation: { required: "Title is required" },
  // },
  {
    fieldName: "Thumbnail Text (EN)",
    fieldValue: "thumnail_text_english",
    className: "col-span-6",
    type: "text",
    name: "thumnail_text_english",
    formType: "input",
    label: "Thumbnail Text (EN)",
    placeholder: "Enter Thumbnail Text (EN)",
    validation: { required: "Thumbnail Text (EN) is required" },
  },
  {
    fieldName: "Thumbnail Text (MS)",
    fieldValue: "thumnail_text_malay",
    className: "col-span-6",
    type: "text",
    name: "thumnail_text_malay",
    formType: "input",
    label: "Thumbnail Text (MS)",
    placeholder: "Enter Thumbnail Text (MS)",
  },
  {
    fieldName: "Thumbnail Text (ZH)",
    fieldValue: "thumnail_text_mandarin",
    className: "col-span-6",
    type: "text",
    name: "thumnail_text_mandarin",
    formType: "input",
    label: "Thumbnail Text (ZH)",
    placeholder: "Enter Thumbnail Text (ZH)",
  },
  {
      fieldName: "Duration",
      fieldValue: "duration",
      className: "col-span-6",
      type: "text",
      name: "duration",
      formType: "input",
      label: "Duration",
      placeholder: "Enter Video Duration (e.g. 6:09)",
      autoFillFromYoutube: true, 
      validation: {
        required: "Duration is required",
        pattern: {
          value: /^[0-9:]+$/,
          message: "Enter a valid duration (e.g. 6:09)",
        },
      },
    },

    {
      fieldName: "Video Link",
      fieldValue: "video_link",
      className: "col-span-6",
      type: "text",
      name: "video_link",
      formType: "input",
      label: "Video Link",
      placeholder: "Enter YouTube Video Link",
      autoYoutubeDuration: true, 
      validation: {
        required: "Video link is required",
        pattern: {
          value:
            /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/,
          message: "Enter a valid YouTube URL",
        },
      },
    },
  {
    fieldName: "Airports",
    fieldValue: "airports",
    className: "col-span-6",
    type: "select",
    name: "airports",
    formType: "multiSelect",
    label: "Airports",
    placeholder: "Select Airports",
    options: [
      { label: "KLIA1", value: "klia1" },
      { label: "KLIA2", value: "klia2" },
      { label: "Langkawi", value: "langkawi" },
      { label: "Penang", value: "penang" },
      { label: "Kuching", value: "kuching" },
      { label: "Kota Kinabalu", value: "kota-kinabalu" },
    ],
    validation: { required: "Airports selection is required" },
  },
  {
    fieldName: "Sequence",
    fieldValue: "sequence",
    className: "col-span-6",
    type: "text",
    name: "sequence",
    formType: "number",
    label: "Sequence",
    placeholder: "Enter Sequence Number",
    validation: {
      required: "Sequence number is required",
      pattern: {
        value: /^[0-9]+$/,
        message: "Only numeric values are allowed",
      },
    },
  },
  {
    fieldName: "Redirect URL",
    fieldValue: "redirectedURL",
    className: "col-span-6",
    type: "text",
    name: "redirectedURL",
    formType: "input",
    label: "Redirect URL",
    placeholder: "Enter Redirect URL (e.g. /node/1390/edit)",
  },
  {
    fieldName: "Thumbnail Image",
    fieldValue: "thumbnail_img",
    className: "col-span-6",
    type: "image",
    name: "thumbnail_img",
    formType: "file",
    label: "Thumbnail Image",
    placeholder: "Upload Thumbnail Image",
    usedFileType: "image/*",
    validation: { required: "Thumbnail image is required" },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Enter Published",
  },
],
travellerEssentialsOverview: [
  {
    fieldName: "Title (EN)",
    fieldValue: "english_title",
    className: "col-span-6",
    type: "text",
    name: "english_title",
    formType: "input",
    label: "Title (EN)",
    placeholder: "Enter English Title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Title (MS)",
    fieldValue: "malay_title",
    className: "col-span-6",
    type: "text",
    name: "malay_title",
    formType: "input",
    label: "Title (MS)",
    placeholder: "Enter Malay Title",
  },
  {
    fieldName: "Title (ZH)",
    fieldValue: "mandarin_title",
    className: "col-span-6",
    type: "text",
    name: "mandarin_title",
    formType: "input",
    label: "Title (ZH)",
    placeholder: "Enter Mandarin Title",
  },
  {
    fieldName: "Description (EN)",
    fieldValue: "description_english",
    className: "col-span-12",
    type: "textarea",
    name: "description_english",
    formType: "richText",
    label: "Description (EN)",
    placeholder: "Enter English Description",
    validation: { required: "Description is required" },
  },
  {
    fieldName: "Description (MS)",
    fieldValue: "description_malay",
    className: "col-span-12",
    type: "textarea",
    name: "description_malay",
    formType: "richText",
    label: "Description (MS)",
    placeholder: "Enter Malay Description",
  },
  {
    fieldName: "Description (ZH)",
    fieldValue: "description_mandarin",
    className: "col-span-12",
    type: "textarea",
    name: "description_mandarin",
    formType: "richText",
    label: "Description (ZH)",
    placeholder: "Enter Mandarin Description",
  },
  // {
  //   fieldName: "Airports",
  //   fieldValue: "airports",
  //   className: "col-span-6",
  //   type: "select",
  //   name: "airports",
  //   formType: "select",
  //   label: "Airports",
  //   placeholder: "Select Airports",
  //   options: [
  //     { label: "KLIA1", value: "klia1" },
  //     { label: "KLIA2", value: "klia2" },
  //     { label: "Langkawi", value: "langkawi" },
  //     { label: "Penang", value: "penang" },
  //     { label: "Kuching", value: "kuching" },
  //     { label: "Kota Kinabalu", value: "kota-kinabalu" },
  //   ],
  //   validation: { required: "At least one airport must be selected" },
  // },
  {
    fieldName: "Main Image",
    fieldValue: "image",
    className: "col-span-6",
    type: "image",
    name: "image",
    formType: "file",
    label: "Main Image",
    placeholder: "Upload Main Image",
    usedFileType: "image/*",
    validation: { required: "Main image is required" },
  },
  // {
  //   fieldName: "Cards",
  //   fieldValue: "cards",
  //   className: "col-span-12",
  //   type: "text",
  //   name: "cards",
  //   formType: "input",
  //   label: "Cards",
  //   placeholder: "Add card details (image, link, document)",
  //   // subFields: [
  //   //   {
  //   //     fieldName: "Card Image",
  //   //     name: "image",
  //   //     type: "file",
  //   //     formType: "file",
  //   //     usedFileType: "image",
  //   //     label: "Card Image",
  //   //     placeholder: "Upload Card Image",
  //   //     validation: { required: "Card image is required" },
  //   //   },
  //   //   {
  //   //     fieldName: "Link",
  //   //     name: "link",
  //   //     type: "text",
  //   //     formType: "input",
  //   //     label: "Link",
  //   //     placeholder: "Enter Card Link",
  //   //   },
  //   //   {
  //   //     fieldName: "Document",
  //   //     name: "document",
  //   //     type: "file",
  //   //     formType: "file",
  //   //     usedFileType: "document",
  //   //     label: "Document (Optional)",
  //   //     placeholder: "Upload PDF / DOC",
  //   //   },
  //   // ],
  // },
  // {
  //     fieldName: "Images",
  //     fieldValue: "cards",
  //     className: "col-span-6",
  //     type: "image",
  //     name: "image",
  //     formType: "multiFile",
  //     usedFileType: 'image/*',
  //     label: "Images",
  //     placeholder: "Enter Image",
  //     validation: { required: "Image is required" },
  //     imageDimension: {
  //       width: 400,
  //       height: 400
  //     }
  //   },
  // {
  //   fieldName: "Published",
  //   fieldValue: "published",
  //   className: "col-span-12",
  //   type: "checkbox",
  //   name: "published",
  //   formType: "checkbox",
  //   label: "Published",
  //   placeholder: "Select if published",
  // },
],
travellerEssentialsList: [
  {
    fieldName: "Image",
    fieldValue: "image",
    className: "col-span-6",
    type: "image",
    name: "image",
    formType: "file",
    label: "Image",
    placeholder: "Upload Image",
    usedFileType: "image/*",
    validation: { required: "Image is required" },
  },
  {
    fieldName: "Link",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Link",
    placeholder: "Enter Link",
    // validation: { required: "Link is required" },
    note: "Kindly enter the link or upload the document"
  },
  {
    fieldName: "Document",
    fieldValue: "document",
    className: "col-span-6",
    type: "pdf",
    name: "document",
    formType: "file",
    label: "Document",
    placeholder: "Upload Document",
    usedFileType: ".pdf",
    // validation: { required: "Document is required" },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Select if published",
  },
],
usefulTipsPageList: [
  {
    fieldName: "Title (EN)",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title (EN)",
    placeholder: "Enter title in English",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Title (MS)",
    fieldValue: "title_malay",
    className: "col-span-6",
    type: "text",
    name: "title_malay",
    formType: "input",
    label: "Title (MS)",
    placeholder: "Enter title in Malay",
  },
  {
    fieldName: "Title (ZH)",
    fieldValue: "title_mandarin",
    className: "col-span-6",
    type: "text",
    name: "title_mandarin",
    formType: "input",
    label: "Title (ZH)",
    placeholder: "Enter title in Mandarin",
  },
  {
    fieldName: "Description (EN)",
    fieldValue: "description",
    className: "col-span-12",
    type: "textarea",
    name: "description",
    formType: "textarea",
    label: "Description (EN)",
    placeholder: "Enter description in English",
    validation: { required: "Description is required" },
  },
  {
    fieldName: "Description (MS)",
    fieldValue: "description_malay",
    className: "col-span-12",
    type: "textarea",
    name: "description_malay",
    formType: "textarea",
    label: "Description (MS)",
    placeholder: "Enter description in Malay",
  },
  {
    fieldName: "Description (ZH)",
    fieldValue: "description_mandarin",
    className: "col-span-12",
    type: "textarea",
    name: "description_mandarin",
    formType: "textarea",
    label: "Description (ZH)",
    placeholder: "Enter description in Mandarin",
  },
  {
    fieldName: "Link",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Link",
    placeholder: "Enter link URL",
    validation: { required: "Link is required" },
  },
  {
    fieldName: "Image",
    fieldValue: "image",
    className: "col-span-6",
    type: "image",
    name: "image",
    formType: "file",
    label: "Image",
    placeholder: "Upload image",
    usedFileType: "image/*",
    validation: { required: "Image is required" },
  },
  {
    fieldName: "Airports",
    fieldValue: "airports",
    className: "col-span-6",
    type: "select",
    name: "airports",
    formType: "multiSelect",
    label: "Airports",
    placeholder: "Select Airports",
    options: [
      { label: "KLIA1", value: "klia1" },
      { label: "KLIA2", value: "klia2" },
      { label: "Langkawi", value: "langkawi" },
      { label: "Penang", value: "penang" },
      { label: "Kuching", value: "kuching" },
      { label: "Kota Kinabalu", value: "kota-kinabalu" },
    ],
    validation: { required: "Select at least one airport" },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Select if published",
  },

],
usefulTipsPageContent: [
  {
    fieldName: "Title (EN)",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title (EN)",
    placeholder: "Enter title in English",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Title (MS)",
    fieldValue: "title_ms",
    className: "col-span-6",
    type: "text",
    name: "title_ms",
    formType: "input",
    label: "Title (MS)",
    placeholder: "Enter title in Malay",
  },
  {
    fieldName: "Title (ZH)",
    fieldValue: "title_zh",
    className: "col-span-6",
    type: "text",
    name: "title_zh",
    formType: "input",
    label: "Title (ZH)",
    placeholder: "Enter title in Mandarin",
  },
],


  cards: [
  {
    fieldName: "Title",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title",
    placeholder: "Enter spotlight title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Banner Image",
    fieldValue: "banner",
    className: "col-span-6",
    name: "banner",
    formType: "file",
    label: "Banner Image",
    placeholder: "Upload banner image",
    usedFileType: "image/*",
    type: "image",
    validation: { required: "Banner image is required" },
  },
  // {
  //   fieldName: "Body",
  //   fieldValue: "body",
  //   className: "col-span-12",
  //   type: "textarea",
  //   name: "body",
  //   formType: "textarea",
  //   label: "Body",
  //   placeholder: "Enter body text",
  //   validation: { required: "Body text is required" },
  // },
  {
    fieldName: "Link",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Link",
    placeholder: "Enter link URL",
  },
  {
    fieldName: "Airports",
    fieldValue: "airports",
    className: "col-span-6",
    type: "select",
    name: "airports",
    formType: "multiSelect",
    label: "Airports",
    placeholder: "Select Airports",
    options: [
      { label: "KLIA1", value: "klia1" },
      { label: "KLIA2", value: "klia2" },
      { label: "Langkawi", value: "langkawi" },
      { label: "Penang", value: "penang" },
      { label: "Kuching", value: "kuching" },
      { label: "Kota Kinabalu", value: "kota-kinabalu" },
    ],
    validation: { required: "Select at least one airport" },
  },
  {
    fieldName: "Sequence",
    fieldValue: "sequence",
    className: "col-span-6",
    type: "text",
    name: "sequence",
    formType: "number",
    label: "Sequence",
    placeholder: "Enter display order (e.g. 1, 2, 3)",
    validation: {
      required: "Sequence is required",
      pattern: {
        value: /^[0-9]+$/,
        message: "Please enter a valid number",
      },
    },
  },
  {
    fieldName: "Published",
    fieldValue: "published",
    className: "col-span-12",
    type: "checkbox",
    name: "published",
    formType: "checkbox",
    label: "Published",
    placeholder: "Select if spotlight is published",
  },
],

pages: [
  {
    fieldName: "Title",
    fieldValue: "title",
    className: "col-span-6",
    type: "text",
    name: "title",
    formType: "input",
    label: "Title",
    placeholder: "Enter page title",
    validation: { required: "Title is required" },
  },
  {
    fieldName: "Banner Image",
    fieldValue: "banner",
    className: "col-span-6",
    name: "banner",
    formType: "file",
    label: "Banner Image",
    placeholder: "Upload banner image",
    usedFileType: "image/*",
    type: "image",
    validation: { required: "Banner image is required" },
  },
  // {
  //   fieldName: "Body",
  //   fieldValue: "body",
  //   className: "col-span-12",
  //   type: "textarea",
  //   name: "body",
  //   formType: "textarea",
  //   label: "Body",
  //   placeholder: "Enter body content",
  //   validation: { required: "Body content is required" },
  // },
  {
    fieldName: "Link",
    fieldValue: "link",
    className: "col-span-6",
    type: "text",
    name: "link",
    formType: "input",
    label: "Link",
    placeholder: "Enter page link URL",
  },
  // {
  //   fieldName: "Airports",
  //   fieldValue: "airports",
  //   className: "col-span-6",
  //   type: "select",
  //   name: "airports",
  //   formType: "select",
  //   label: "Airports",
  //   placeholder: "Select Airports",
  //   options: [
  //     { label: "KLIA1", value: "klia1" },
  //     { label: "KLIA2", value: "klia2" },
  //     { label: "Langkawi", value: "langkawi" },
  //     { label: "Penang", value: "penang" },
  //     { label: "Kuching", value: "kuching" },
  //     { label: "Kota Kinabalu", value: "kota-kinabalu" },
  //   ],
  //   validation: { required: "Select at least one airport" },
  // },
  // {
  //   fieldName: "Sequence",
  //   fieldValue: "sequence",
  //   className: "col-span-6",
  //   type: "text",
  //   name: "sequence",
  //   formType: "number",
  //   label: "Sequence",
  //   placeholder: "Enter display order (e.g. 1, 2, 3)",
  //   validation: {
  //     required: "Sequence is required",
  //     pattern: {
  //       value: /^[0-9]+$/,
  //       message: "Please enter a valid number",
  //     },
  //   },
  // },
  // {
  //   fieldName: "Published",
  //   fieldValue: "published",
  //   className: "col-span-12",
  //   type: "checkbox",
  //   name: "published",
  //   formType: "checkbox",
  //   label: "Published",
  //   placeholder: "Select if published",
  // },
]

};

export default getHomepageDrillDownData;